import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { AtlasArticle, ArticleVersion, BibliographyEntry } from '../types/atlas';
import ReactMarkdown, { Components } from 'react-markdown';
import { PdfLightbox } from './PdfLightbox';
import { ImageLightbox } from './ImageLightbox';
import { InfographicLightbox } from './InfographicLightbox';
import { useCitation, getResourceClass } from '../hooks/useCitation';
import { atlasClient } from '../api/atlasClient';
import { authenticatedFetch, parseJsonResponse } from '../api/apiClient';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';
import { InlineSlideViewer } from './InlineSlideViewer';
import { normalizeMarkdownBullets } from '../features/spark/utils/markdown';
import tagStyles from '../features/spark/styles/TagContextSelector.module.css';
import { formatContextToken, normalizeContextMapping } from '../utils/context';


const buildInfographicImageUrl = (articleFqn: string, exportId: string, filename: string): string =>
  `/api/atlas/articles/${encodeURIComponent(articleFqn)}/exports/${encodeURIComponent(exportId)}/files/${encodeURIComponent(filename)}?type=png`;

// Global image cache (persists across component instances)
const imageCache: Record<string, string> = {};

// Book icon for learnings panel
const BookIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

// Copy icon
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// Chevron icon
const ChevronIcon = ({ isUp }: { isUp: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ transform: isUp ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

interface LearningItemProps {
  title: string;
  content: string;
  markdownComponents: Components;
  markdownPlugins: any[];
}

const LearningItem: React.FC<LearningItemProps> = ({
  title,
  content,
  markdownComponents,
  markdownPlugins
}) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${title}\n\n${content}`);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="learning-item">
      <div className="learning-item-content">
        <h4 className="learning-item-title">{title}</h4>
        <div className="learning-item-text">
          <ReactMarkdown
            components={markdownComponents}
            remarkPlugins={markdownPlugins}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
      <button className="learning-item-copy" onClick={handleCopy} title="Copy to clipboard">
        <CopyIcon />
        <span>Copy</span>
      </button>
    </div>
  );
};

interface LearningsPanelProps {
  learnings: Array<{ title: string; content: string }>;
  isExpanded: boolean;
  onToggle: () => void;
  markdownComponents: Components;
  markdownPlugins: any[];
}

const LearningsPanel: React.FC<LearningsPanelProps> = ({
  learnings,
  isExpanded,
  onToggle,
  markdownComponents,
  markdownPlugins,
}) => {
  if (!learnings || learnings.length === 0) {
    return null;
  }

  return (
    <div className={`learnings-panel ${isExpanded ? 'expanded' : ''}`}>
      <button className="learnings-panel-header" onClick={onToggle}>
        <div className="learnings-panel-header-left">
          <BookIcon />
          <span>Learnings</span>
        </div>
        <ChevronIcon isUp={isExpanded} />
      </button>
      {isExpanded && (
        <div className="learnings-panel-content">
          {learnings.map((learning, index) => (
            <LearningItem
              key={`${index}-${learning.title}`}
              title={learning.title}
              content={learning.content}
              markdownComponents={markdownComponents}
              markdownPlugins={markdownPlugins}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Chapter Sidebar
interface ChapterSidebarProps {
  sections: SectionItem[];
  activeSectionId: string | null;
  onSectionClick: (sectionId: string) => void;
}

const ChapterSidebar: React.FC<ChapterSidebarProps> = ({ sections, activeSectionId, onSectionClick }) => (
  <nav className="chapter-sidebar" aria-label="Article chapters">
    <ul className="chapter-sidebar-list">
      {sections.map((section) => (
        <li key={section.id}>
          <button
            className={`chapter-sidebar-item ${section.id === activeSectionId ? 'active' : ''}`}
            onClick={() => onSectionClick(section.id)}
          >
            {section.title}
          </button>
        </li>
      ))}
    </ul>
  </nav>
);

// Version History Panel
interface VersionHistoryPanelProps {
  versions: ArticleVersion[];
  selectedVersionDate: string | null;
  onSelectVersion: (date: string) => void;
  onClose: () => void;
}

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  versions,
  selectedVersionDate,
  onSelectVersion,
  onClose,
}) => {
  const reversedVersions = [...versions].reverse();
  const latestDate = versions.length > 0 ? versions[versions.length - 1].date : null;
  const effectiveSelected = selectedVersionDate || latestDate;

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <div className="version-history-backdrop" onClick={onClose} />
      <div className="version-history-panel">
        <div className="version-history-header">
          <h3>Version history</h3>
          <button className="version-history-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="version-history-list">
          {reversedVersions.map((version, idx) => {
            const versionNum = versions.length - idx;
            const isSelected = version.date === effectiveSelected;
            return (
              <button
                key={version.date}
                className={`version-history-item ${isSelected ? 'active' : ''}`}
                onClick={() => {
                  onSelectVersion(version.date);
                  onClose();
                }}
              >
                <span className="version-history-item-label">Version {versionNum}</span>
                <span className="version-history-item-date">{formatDate(version.date)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

interface SectionItem {
  id: string;
  title: string;
  text: string;
  learnings: Array<{ title: string; content: string }>;
  reasoning: string;
}

const formatFqnName = (fqn: string): string => {
  const baseId = fqn.split('@')[1]?.split('/').pop() || fqn;
  return baseId
    .split('_')
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ');
};

interface TagDimensionRow {
  dimensionFqn: string;
  matchingTags: string[];
  relevantTags: string[];
}

const resolveTagNames = (
  tagFqns: string[] | undefined,
  elementNameByFqn: Map<string, string>
): string[] =>
  Array.from(
    new Set((tagFqns || []).map((tagFqn) => elementNameByFqn.get(tagFqn) || formatFqnName(tagFqn)))
  ).sort((left, right) => left.localeCompare(right));

const buildTagRows = (
  matchingTagMap: Record<string, string[]> | undefined,
  relevantTagMap: Record<string, string[]> | undefined,
  elementNameByFqn: Map<string, string>
): TagDimensionRow[] => {
  const dimensionFqns = Array.from(
    new Set([
      ...Object.keys(matchingTagMap || {}),
      ...Object.keys(relevantTagMap || {}),
    ])
  ).sort((left, right) => left.localeCompare(right));

  return dimensionFqns
    .map((dimensionFqn) => {
      const matchingTags = resolveTagNames(matchingTagMap?.[dimensionFqn], elementNameByFqn);
      const relevantTags = resolveTagNames(relevantTagMap?.[dimensionFqn], elementNameByFqn)
        .filter((tagName) => !matchingTags.includes(tagName));

      return {
        dimensionFqn,
        matchingTags,
        relevantTags,
      };
    })
    .filter((row) => row.matchingTags.length > 0 || row.relevantTags.length > 0);
};

const ArticleTagRow: React.FC<{
  matchingTags: string[];
  relevantTags: string[];
}> = ({ matchingTags, relevantTags }) => {
  if (matchingTags.length === 0 && relevantTags.length === 0) {
    return null;
  }

  return (
    <div className="viewer-tag-chip-row">
      {matchingTags.map((tagName) => (
        <span
          key={tagName}
          className={`${tagStyles.tagChip} viewer-tag-chip-present`}
        >
          <span className={tagStyles.tagChipLabel}>{tagName}</span>
        </span>
      ))}
      {relevantTags.map((tagName) => (
        <span
          key={`relevant:${tagName}`}
          className={`${tagStyles.tagChip} viewer-tag-chip-relevant`}
          style={{
            backgroundColor: '#f8fafc',
            color: '#667085',
            borderColor: '#d0d5dd',
          }}
        >
          <span className={tagStyles.tagChipLabel}>{tagName}</span>
        </span>
      ))}
    </div>
  );
};

export interface UnifiedViewerHandle {
  downloadWord: () => Promise<void>;
  toggleInfographic: () => Promise<void>;
  isDownloadingWord: boolean;
  isGeneratingInfographic: boolean;
  infographicCountdown: number | null;
  isInfographicViewActive: boolean;
  showInfographic: boolean;
}

export interface UnifiedViewerProps {
  article: AtlasArticle;
  initialChapter?: string;
  hideLearnings?: boolean;
  onNavigateToArticle?: (fqn: string, initialChapter?: string) => void;
  isVersionHistoryOpen?: boolean;
  onCloseVersionHistory?: () => void;
}

export const UnifiedViewer = forwardRef<UnifiedViewerHandle, UnifiedViewerProps>(({
  article,
  initialChapter,
  hideLearnings = false,
  onNavigateToArticle,
  isVersionHistoryOpen = false,
  onCloseVersionHistory,
}, ref) => {
  const bibliography = article.bibliography as Record<string, BibliographyEntry> | undefined;

  // Version management
  const sortedVersions = [...article.versions];
  const [selectedVersionDate, setSelectedVersionDate] = useState<string | null>(null);

  const currentVersion: ArticleVersion | null = selectedVersionDate
    ? sortedVersions.find(v => v.date === selectedVersionDate) || (sortedVersions.length > 0 ? sortedVersions[sortedVersions.length - 1] : null)
    : (sortedVersions.length > 0 ? sortedVersions[sortedVersions.length - 1] : null);

  // UI state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [pdfLightbox, setPdfLightbox] = useState<{ url: string; page?: number } | null>(null);
  const [imageLightbox, setImageLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [isDownloadingWord, setIsDownloadingWord] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const [isHeroImageLoading, setIsHeroImageLoading] = useState(false);

  // Export format detection state
  const [supportedFormats, setSupportedFormats] = useState<string[]>([]);
  const [isLoadingFormats, setIsLoadingFormats] = useState(true);
  const [tagCatalog, setTagCatalog] = useState<{
    elementNameByFqn: Map<string, string>;
  }>({
    elementNameByFqn: new Map(),
  });

  // Slide viewer state (kept for potential re-enablement)
  const [isSlideViewActive] = useState(false);
  const [slideViewData] = useState<{
    exportId: string;
    fileCount: number;
    articleFqn: string;
  } | null>(null);

  // Infographic viewer state
  const [isGeneratingInfographic, setIsGeneratingInfographic] = useState(false);
  const [infographicCountdown, setInfographicCountdown] = useState<number | null>(null);
  const [isInfographicViewActive, setIsInfographicViewActive] = useState(false);
  const [infographicData, setInfographicData] = useState<{
    exportId: string;
    filename: string;
    articleFqn: string;
  } | null>(null);

  const [isDownloadingImage, setIsDownloadingImage] = useState(false);

  // Debounce ref for infographic
  const lastInfographicExportTimeRef = useRef<number>(0);
  const EXPORT_DEBOUNCE_MS = 2000;

  // Scroll tracking refs
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // Countdown timer for infographic generation
  useEffect(() => {
    if (isGeneratingInfographic) {
      setInfographicCountdown(60);
      const interval = setInterval(() => {
        setInfographicCountdown(prev => {
          if (prev === null || prev <= 0) return 0;
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setInfographicCountdown(null);
    }
  }, [isGeneratingInfographic]);

  // Use citation hook with article bibliography
  const { markdownComponents: citationMarkdownComponents, CitationPortal, handleReferenceClick: citationHandleReferenceClick } = useCitation({
    bibliography,
    onNavigateToArticle,
  });

  // Build sections from current version
  const sections: SectionItem[] = React.useMemo(() => {
    if (!currentVersion) return [];

    const summarySection: SectionItem = {
      id: 'summary',
      title: 'Summary',
      text: currentVersion.summary?.text || '',
      learnings: currentVersion.summary?.learnings || [],
      reasoning: currentVersion.summary?.reasoning || ''
    };

    const chapters = Array.isArray(currentVersion.chapters) ? currentVersion.chapters : Object.values(currentVersion.chapters || {});

    const chapterSections: SectionItem[] = chapters.map((chapter: any, idx: number) => ({
      id: chapter.topic_fqn || `chapter-${idx}`,
      title: chapter.title,
      text: chapter.text,
      learnings: chapter.learnings,
      reasoning: chapter.reasoning
    }));

    const referencesSection: SectionItem = {
      id: 'references',
      title: 'References',
      text: '',
      learnings: [],
      reasoning: ''
    };

    return [summarySection, ...chapterSections, referencesSection];
  }, [currentVersion]);

  // Set initial active section
  useEffect(() => {
    if (sections.length > 0 && !activeSectionId) {
      setActiveSectionId(sections[0].id);
    }
  }, [sections, activeSectionId]);

  // Scroll to initial chapter on mount
  useEffect(() => {
    if (initialChapter && sections.some(s => s.id === initialChapter)) {
      setTimeout(() => {
        const el = sectionRefs.current[initialChapter];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, [initialChapter, sections]);

  // IntersectionObserver for scroll tracking
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const scrollContainer = contentScrollRef.current;
    if (!scrollContainer || sections.length === 0) return;

    // Defer to next frame so all section refs are populated after render commit
    const frameId = requestAnimationFrame(() => {
      const els = Object.values(sectionRefs.current).filter(Boolean) as HTMLDivElement[];
      if (els.length === 0) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length > 0) {
            const id = visible[0].target.getAttribute('data-section-id');
            if (id) setActiveSectionId(id);
          }
        },
        {
          root: scrollContainer,
          rootMargin: '-10% 0px -80% 0px',
          threshold: 0,
        }
      );

      els.forEach(el => observerRef.current!.observe(el));
    });

    return () => {
      cancelAnimationFrame(frameId);
      observerRef.current?.disconnect();
    };
  }, [sections]);

  // Scroll to top when version changes
  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, [currentVersion]);

  // Reset expanded learnings when hidden
  useEffect(() => {
    if (hideLearnings) {
      setExpandedSections({});
    }
  }, [hideLearnings]);

  // Hero image loading with cache
  useEffect(() => {
    const loadHeroImage = async () => {
      const heroId = article.subject?.hero_image;
      if (heroId) {
        try {
          setIsHeroImageLoading(true);
          if (imageCache[heroId]) {
            setHeroImageUrl(imageCache[heroId]);
            return;
          }
          const url = await atlasClient.getAssetUrl(heroId, { full: true });
          imageCache[heroId] = url;
          setHeroImageUrl(url);
        } catch (error) {
          console.error('Failed to load hero image:', error);
          setHeroImageUrl(null);
        } finally {
          setIsHeroImageLoading(false);
        }
      } else {
        setHeroImageUrl(null);
        setIsHeroImageLoading(false);
      }
    };

    loadHeroImage();
  }, [article.subject?.hero_image]);

  // Check supported export formats when article changes
  useEffect(() => {
    const checkSupportedFormats = async () => {
      if (!article.fqn) {
        setSupportedFormats([]);
        setIsLoadingFormats(false);
        return;
      }

      setIsLoadingFormats(true);
      try {
        const response = await authenticatedFetch(
          `/api/atlas/articles/${encodeURIComponent(article.fqn)}/export/formats`
        ).then(parseJsonResponse);

        setSupportedFormats(response.supported_formats || []);
      } catch (error) {
        console.error('Failed to check supported formats:', error);
        setSupportedFormats([]);
      } finally {
        setIsLoadingFormats(false);
      }
    };

    checkSupportedFormats();
  }, [article.fqn]);

  const hasPosthocTags = Boolean(
    Object.keys(article.latest_version_posthoc_tags || {}).length ||
    Object.keys(article.latest_version_posthoc_related_tags || {}).length
  );
  const contextMapping = React.useMemo(() => normalizeContextMapping(article.context), [article.context]);
  const hasContextBadges = Object.keys(contextMapping).length > 0;
  const needsElementCatalog = hasPosthocTags || hasContextBadges;

  useEffect(() => {
    if (!needsElementCatalog) {
      setTagCatalog({
        elementNameByFqn: new Map(),
      });
      return;
    }

    let cancelled = false;

    const loadTagCatalog = async () => {
      try {
        const dimensions = await atlasClient.getDimensionsAndElements();
        if (cancelled) {
          return;
        }

        const elementNameByFqn = new Map<string, string>();

        dimensions.forEach((dimension) => {
          dimension.elements.forEach((element) => {
            elementNameByFqn.set(element.fqn, element.name || formatFqnName(element.fqn));
          });
        });

        setTagCatalog({ elementNameByFqn });
      } catch (error) {
        console.error('Failed to load tag catalog:', error);
        if (!cancelled) {
          setTagCatalog({
            elementNameByFqn: new Map(),
          });
        }
      }
    };

    loadTagCatalog();

    return () => {
      cancelled = true;
    };
  }, [needsElementCatalog]);

  const contextBadgeNames = React.useMemo(
    () =>
      Object.entries(contextMapping)
        .sort(([leftDimensionId], [rightDimensionId]) => leftDimensionId.localeCompare(rightDimensionId))
        .map(([dimensionId, elementId]) => {
          const elementFqn = `prism_element@${dimensionId}/${elementId}`;
          return tagCatalog.elementNameByFqn.get(elementFqn) || formatContextToken(elementId);
        }),
    [contextMapping, tagCatalog.elementNameByFqn]
  );

  const tagRows = React.useMemo(
    () =>
      buildTagRows(
        article.latest_version_posthoc_tags,
        article.latest_version_posthoc_related_tags,
        tagCatalog.elementNameByFqn
      ),
    [
      article.latest_version_posthoc_related_tags,
      article.latest_version_posthoc_tags,
      tagCatalog.elementNameByFqn,
    ]
  );

  // Derived values
  const markdownPlugins = [remarkGfm];

  const infographicImageUrl = infographicData
    ? buildInfographicImageUrl(infographicData.articleFqn, infographicData.exportId, infographicData.filename)
    : null;

  // Format version display
  const formatVersionDisplay = () => {
    if (!currentVersion) return '';
    const versionNum = sortedVersions.indexOf(currentVersion) + 1;
    try {
      const date = new Date(currentVersion.date);
      if (isNaN(date.getTime())) return `Version ${versionNum}`;
      return `Version ${versionNum}, ${date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    } catch {
      return `Version ${versionNum}`;
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleSidebarSectionClick = useCallback((sectionId: string) => {
    setActiveSectionId(sectionId);
    const el = sectionRefs.current[sectionId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Download word handler
  const handleDownloadWord = useCallback(async () => {
    if (!article.fqn || isDownloadingWord) return;
    setIsDownloadingWord(true);
    try {
      const blob = await atlasClient.downloadArticleExport(article.fqn, 'docx');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const downloadFilename = `${article.name || 'article'}.docx`;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading article as Word:', error);
      toast.error('Failed to download DOC');
    } finally {
      setIsDownloadingWord(false);
    }
  }, [article.fqn, article.name, isDownloadingWord]);

  // Handle toggling infographic lightbox
  const handleToggleInfographicView = useCallback(async () => {
    if (isInfographicViewActive) {
      setIsInfographicViewActive(false);
      return;
    }

    if (!infographicData) {
      const now = Date.now();
      if (now - lastInfographicExportTimeRef.current < EXPORT_DEBOUNCE_MS) return;
      lastInfographicExportTimeRef.current = now;

      setIsGeneratingInfographic(true);
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 90_000);
        const response = await authenticatedFetch(
          `/api/atlas/articles/${encodeURIComponent(article.fqn)}/export/png`,
          { signal: controller.signal }
        ).then(parseJsonResponse);
        clearTimeout(timeoutId);

        setInfographicData({
          exportId: response.export_id,
          filename: response.filename,
          articleFqn: article.fqn
        });
        setIsInfographicViewActive(true);
      } catch (error) {
        console.error('Failed to generate infographic:', error);
        toast.error('Failed to generate infographic. Please try again.');
      } finally {
        setIsGeneratingInfographic(false);
      }
    } else {
      setIsInfographicViewActive(true);
    }
  }, [article.fqn, infographicData, isInfographicViewActive]);

  // Handle downloading infographic image
  const handleDownloadImage = useCallback(async () => {
    if (!infographicImageUrl || isDownloadingImage) return;
    setIsDownloadingImage(true);
    try {
      const response = await authenticatedFetch(infographicImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${article.name || 'infographic'}.png`;
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
      toast.error('Failed to download image');
    } finally {
      setIsDownloadingImage(false);
    }
  }, [infographicImageUrl, isDownloadingImage, article.name]);

  // Expose imperative handle for parent (ViewerView) to call
  useImperativeHandle(ref, () => ({
    downloadWord: handleDownloadWord,
    toggleInfographic: handleToggleInfographicView,
    isDownloadingWord,
    isGeneratingInfographic,
    infographicCountdown,
    isInfographicViewActive,
    showInfographic: !isLoadingFormats && supportedFormats.includes('png'),
  }), [handleDownloadWord, handleToggleInfographicView, isDownloadingWord, isGeneratingInfographic, infographicCountdown, isInfographicViewActive, isLoadingFormats, supportedFormats]);

  // Reference components
  const ReferenceEntry: React.FC<{
    reference: string;
    entry: BibliographyEntry;
    onReferenceClick: (ref: string) => void;
  }> = ({ reference, entry, onReferenceClick }) => {
    const pageRefs = entry.details ?
      Object.entries(entry.details)
        .sort((a, b) => {
          const numA = parseInt(a[1].label, 10);
          const numB = parseInt(b[1].label, 10);
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
          return a[1].label.localeCompare(b[1].label);
        })
        .map(([_, detail]) => detail.label)
        .join(', ') : '';

    const resourceClass = getResourceClass(entry.type);

    return (
      <div
        className="reference-entry"
        onClick={() => onReferenceClick(reference)}
      >
        <div className="reference-header">
          <div className="reference-title">{entry.title}</div>
        </div>
        <div className="reference-meta">
          <span className={`reference-class-chip reference-class-chip-${resourceClass.toLowerCase()}`}>
            {resourceClass}
          </span>
          <span className="reference-meta-text">
            {entry.source}
            {entry.date && ` | ${entry.date}`}
            {pageRefs && ` | pages: ${pageRefs}`}
          </span>
        </div>
        {entry.comment && (
          <div className="reference-comments">
            {entry.comment}
          </div>
        )}
      </div>
    );
  };

  const ReferencesSection: React.FC<{
    bibliography: Record<string, BibliographyEntry>;
    onReferenceClick: (ref: string) => void;
  }> = ({ bibliography: bib, onReferenceClick }) => {
    const sortedReferences = Object.entries(bib)
      .sort(([, entryA], [, entryB]) => {
        const dateA = entryA.date ? new Date(entryA.date).getTime() : 0;
        const dateB = entryB.date ? new Date(entryB.date).getTime() : 0;
        return dateB - dateA;
      });

    return (
      <div className="references-section">
        {sortedReferences.map(([reference, entry]) => (
          <ReferenceEntry
            key={reference}
            reference={reference}
            entry={entry}
            onReferenceClick={onReferenceClick}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="viewer-body fade-in">
      {/* Left sidebar - chapter index */}
      {!isSlideViewActive && !isInfographicViewActive && (
        <ChapterSidebar
          sections={sections}
          activeSectionId={activeSectionId}
          onSectionClick={handleSidebarSectionClick}
        />
      )}

      {/* Main content area */}
      <div className="viewer-content-scroll" ref={contentScrollRef}>
        {/* Conditionally render slide viewer or normal content */}
        {isSlideViewActive && slideViewData ? (
          <InlineSlideViewer
            exportId={slideViewData.exportId}
            iterationFqn={slideViewData.articleFqn}
            fileCount={slideViewData.fileCount}
          />
        ) : (
          <>
            {/* Hero section: image + title side by side */}
            <div className="viewer-hero-section">
              {isHeroImageLoading ? (
                <div className="viewer-hero-image-container">
                  <div className="hero-image-loading">
                    <div className="loading-spinner" />
                  </div>
                </div>
              ) : heroImageUrl ? (
                <div className="viewer-hero-image-container">
                  <img
                    src={heroImageUrl}
                    alt={article.subject?.name || 'Hero image'}
                    className="viewer-hero-image"
                  />
                </div>
              ) : null}
              <div className="viewer-hero-text">
                {contextBadgeNames.length > 0 && (
                  <div className="viewer-context-badge-row">
                    {contextBadgeNames.map((badgeName) => (
                      <span key={badgeName} className="viewer-context-badge">
                        {badgeName}
                      </span>
                    ))}
                  </div>
                )}
                <h1 className="viewer-hero-title">{article.subject?.name || article.name}</h1>
                <span className="viewer-hero-version">{formatVersionDisplay()}</span>
                {tagRows.length > 0 && (
                  <div className="viewer-tag-section">
                    {tagRows.map((row) => (
                      <ArticleTagRow
                        key={row.dimensionFqn}
                        matchingTags={row.matchingTags}
                        relevantTags={row.relevantTags}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* All sections rendered vertically */}
            {sections.map((section) => (
              <div
                key={section.id}
                ref={(el) => { sectionRefs.current[section.id] = el; }}
                data-section-id={section.id}
                className="viewer-section"
              >
                <h2 className="viewer-section-title">{section.title}</h2>

                {section.id === 'references' ? (
                  <ReferencesSection
                    bibliography={bibliography || {}}
                    onReferenceClick={(r) => citationHandleReferenceClick(r)}
                  />
                ) : (
                  <>
                    {!hideLearnings && section.learnings?.length > 0 && (
                      <LearningsPanel
                        learnings={section.learnings}
                        isExpanded={expandedSections[section.id]}
                        onToggle={() => toggleSection(section.id)}
                        markdownComponents={citationMarkdownComponents}
                        markdownPlugins={markdownPlugins}
                      />
                    )}
                    <div className="markdown-content">
                      <ReactMarkdown
                        components={citationMarkdownComponents}
                        remarkPlugins={markdownPlugins}
                      >
                        {normalizeMarkdownBullets(section.text.replace(/^#\s+[^\n]+\n/, ''))}
                      </ReactMarkdown>
                    </div>
                  </>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Version history slide-out panel */}
      {isVersionHistoryOpen && (
        <VersionHistoryPanel
          versions={sortedVersions}
          selectedVersionDate={selectedVersionDate}
          onSelectVersion={setSelectedVersionDate}
          onClose={onCloseVersionHistory || (() => {})}
        />
      )}

      {pdfLightbox?.url && (
        <PdfLightbox
          url={pdfLightbox.url}
          initialPage={pdfLightbox.page}
          onClose={() => {
            window.URL.revokeObjectURL(pdfLightbox.url);
            setPdfLightbox(null);
          }}
        />
      )}
      {imageLightbox && (
        <ImageLightbox
          src={imageLightbox.src}
          alt={imageLightbox.alt}
          onClose={() => setImageLightbox(null)}
        />
      )}
      {isInfographicViewActive && infographicImageUrl && (
        <InfographicLightbox
          src={infographicImageUrl}
          alt="Article Infographic"
          onClose={() => setIsInfographicViewActive(false)}
          onDownloadImage={handleDownloadImage}
          isDownloadingImage={isDownloadingImage}
        />
      )}
      <CitationPortal />
    </div>
  );
});

UnifiedViewer.displayName = 'UnifiedViewer';
